import { COMMENTS_ORDER } from "../../../../../constants/BrandedCommunity/Comments";
import { ICommentData } from "../../../../../components/BrandedCommunity/Post/components/Comment/Comment.types";
import { IUseCommentsList } from "./useCommentsList.types";
import { useState } from "react";
import { useToggleState } from "../../../../common/dataTypes/boolean";
import { checkIsFunction } from "../../../../common/dataTypes/function";
import { getErrorMessageFromApiError } from "../../../../common/api";
import { checkIsArrayEmpty } from "../../../../common/dataTypes/array";
import { COMMENT_STATUS } from "../../../../../features/Offerings/modules/RecordedContent/CommentSection/CommentSection.data";

const page_sizes = {
  first_call: 3,
  subsequent_calls: 10,
};

export const useCommentsList = ({
  commentsOrder,
  userUuid,
  isCreator,
  highlightedComment,
  setHighlightedComment,
  highlightedReply,
  setHighlightedReply,
  callSubmitApi,
  onSubmitSuccess,
  callGetCommentsApi,
  callLikeApi,
  callEditApi,
  callDeleteApi,
  notifySuccess,
  notifyError,
}: IUseCommentsList.IUseCommentsListProps): IUseCommentsList.IUseCommentsListReturn => {
  const [isLoading, startLoading, stopLoading] = useToggleState(false);
  const [totalComments, setTotalComments] = useState<number | undefined>(
    undefined,
  );
  const [data, setData] = useState<Array<ICommentData>>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(page_sizes.first_call);

  /**
   * This function is can be used for reloading the comments to page 1 and initial state.
   * It sets the loading state, fetches comments, and updates the local state with the new comments.
   * It also accepts an optional success callback to be executed after the comments are successfully loaded.
   * */
  const reloadComments = (args: { successCallback?: () => void } = {}) => {
    if (isLoading) return;
    startLoading();
    const newPageSize = page_sizes.first_call;
    const newPage = 1;
    callGetCommentsApi({ page: newPage, page_size: newPageSize })
      .then((response) => {
        const responseData = response?.data || {}; // Get response data
        const newData: Array<ICommentData> = responseData.comments || []; // Extract comments
        const newTotalComments: number = responseData.total || 0; // Extract total comments count
        setPageSize(newPageSize); // Update the local page size state
        setTotalComments(newTotalComments); // Update the total comments count
        setData([...newData]); // Set the new comments data
        setPage(newPage); // Reset to the first page
        if (args.successCallback) args.successCallback(); // Call success callback if provided
      })
      .catch((e) => {
        notifyError(
          getErrorMessageFromApiError(e) ||
            "An error occured, please try after some time",
        );
      })
      .finally(stopLoading);
  };

  const loadComments = (args: { successCallback?: () => void } = {}) => {
    if (isLoading) return;
    startLoading();

    // initial
    let newPage = 0;
    let newPageSize = page_sizes.first_call;
    if (page === 0) {
      // fetch first page with page size 3
      newPage = 1;
      newPageSize = page_sizes.first_call;
    } else if (page === 1 && pageSize === page_sizes.first_call) {
      // fetch first page with page size 10
      newPage = 1;
      newPageSize = page_sizes.subsequent_calls;
    } else {
      newPage = page + 1;
      newPageSize = page_sizes.subsequent_calls;
    }

    callGetCommentsApi({ page: newPage, page_size: newPageSize })
      .then((response) => {
        const responseData = response?.data || {};
        const newData: Array<ICommentData> = responseData.comments || [];
        const newTotalComments: number = responseData.total || 0;
        setPageSize(newPageSize);
        setTotalComments(newTotalComments);
        if (newPage === 1) {
          setData([...newData]);
        } else {
          setData([...data, ...newData]);
        }
        if (args.successCallback) args.successCallback();
        setPage(newPage);
      })
      .catch((e) => {
        notifyError(
          getErrorMessageFromApiError(e) ||
            "An error occured, please try after some time",
        );
      })
      .finally(stopLoading);
  };

  const onSubmit: IUseCommentsList.IUseCommentsListReturn["onSubmit"] = ({
    comment_body,
    attachments,
    successCallback,
  }) => {
    if (isLoading) return;

    const processedCommentBody = (comment_body || "").trim();

    /**
     * Since attachments are introduced so a comment minimum criteria is either
     * it should have a comment text or should have some attachments.
     */
    const isMinimumCommentCriteriaMissed =
      !processedCommentBody && checkIsArrayEmpty(attachments);

    if (isMinimumCommentCriteriaMissed) {
      notifyError("Comment cannot be empty!");
      return;
    }

    startLoading();

    callSubmitApi({ comment: processedCommentBody, attachments })
      .then(() => {
        // COMMENTS
        // refetch page 1
        if (commentsOrder === COMMENTS_ORDER.DESCENDING) {
          // initial
          let newPage = 0;
          let newPageSize = page_sizes.first_call;
          if (data.length < page_sizes.first_call) {
            // fetch first page with page size 3
            newPage = 1;
            newPageSize = page_sizes.first_call;
          } else {
            // fetch first page with page size 10
            newPage = 1;
            newPageSize = page_sizes.subsequent_calls;
          }

          callGetCommentsApi({
            page: newPage,
            page_size: newPageSize,
          })
            .then((response) => {
              const responseData = response?.data || {};
              const newData: Array<ICommentData> = responseData.comments || [];
              const newTotalComments: number = responseData.total || 0;
              setPageSize(newPageSize);
              setTotalComments(newTotalComments);
              setData([...newData]);
              setPage(newPage);

              /**Removing the comment from top and reply from top in case someone commented */
              if (highlightedComment && setHighlightedComment)
                setHighlightedComment(undefined);
              if (highlightedReply && setHighlightedReply)
                setHighlightedReply(undefined);
            })
            .finally(stopLoading);
        } else {
          // REPLIES
          // refetch current page
          /**
           * TODO: 2 replies list
           *  1. loaded
           *  2. posted
           * on load more, remove items from posted array that are there in newly loaded 10 items
           */
          let newPage = page;
          if (newPage === 0) {
            newPage = 1;
          }
          callGetCommentsApi({
            page: newPage,
            page_size: pageSize,
          })
            .then((response) => {
              const responseData = response?.data || {};
              const newDataInResponse: Array<ICommentData> =
                responseData.comments || [];
              const newTotalComments: number = responseData.total || 0;
              setTotalComments(newTotalComments);
              if (newPage === 1) {
                setData([...newDataInResponse]);
              } else {
                let newData: ICommentData[] = [];
                const firstCommentUuidInDataArg = newDataInResponse[0]?.uuid;
                const indexToBeReplacedFrom = data.findLastIndex(
                  (i) => i.uuid === firstCommentUuidInDataArg,
                );
                if (indexToBeReplacedFrom >= 0) {
                  newData = [
                    ...data.slice(0, indexToBeReplacedFrom),
                    ...newDataInResponse,
                  ];
                }
                setData(newData);
              }
            })
            .finally(stopLoading);
        }

        if (onSubmitSuccess && checkIsFunction(onSubmitSuccess))
          onSubmitSuccess();

        if (successCallback && checkIsFunction(successCallback))
          successCallback();
      })
      .catch((e) => {
        notifyError(
          getErrorMessageFromApiError(e) ||
            "An error occured, please try after some time",
        );
        stopLoading();
      });
  };

  const [isProcessingComment, startProcessingComment, stopProcessingComment] =
    useToggleState();

  const onLikeClick: IUseCommentsList.IUseCommentsListReturn["onLikeClick"] = ({
    comment_uuid,
  }) => {
    if (isProcessingComment) return;
    startProcessingComment();
    callLikeApi({ comment_uuid })
      .then((response) => {
        const hasLikedComment = !!response?.data?.has_liked;

        const getNewLikesCount = (comment: ICommentData) => {
          let newLikesCount = comment.likes_count;
          if (hasLikedComment !== !!comment.has_liked_comment) {
            newLikesCount = hasLikedComment
              ? comment.likes_count + 1
              : comment.likes_count - 1;
          }
          return newLikesCount;
        };

        /** Updating highlighted Comment and reply in case they are liked */
        if (setHighlightedComment && comment_uuid == highlightedComment?.uuid)
          setHighlightedComment({
            ...highlightedComment,
            has_liked_comment: hasLikedComment,
            likes_count: getNewLikesCount(highlightedComment),
          });
        else if (setHighlightedReply && comment_uuid == highlightedReply?.uuid)
          setHighlightedReply({
            ...highlightedReply,
            has_liked_comment: hasLikedComment,
            likes_count: getNewLikesCount(highlightedReply),
          });
        else {
          const newData = data.map((i) => {
            if (i.uuid !== comment_uuid) {
              return i;
            }
            return {
              ...i,
              has_liked_comment: hasLikedComment,
              likes_count: getNewLikesCount(i),
            };
          });
          setData(newData);
        }
      })
      .catch((e) => {
        notifyError(
          getErrorMessageFromApiError(e) ||
            "An error occured, please try after some time",
        );
      })
      .finally(stopProcessingComment);
  };

  const onUpdate: IUseCommentsList.IUseCommentsListReturn["onUpdate"] = ({
    status,
    commentData,
    attachments,
    successCallback,
  }) => {
    /**Either status change is passed from isCreator or user is trying to edit a comment */
    if (!((isCreator && status) || userUuid === commentData.commenter_username))
      return;

    if (isProcessingComment) return;

    const processedCommentBody = (commentData.comment || "").trim();
    /**
     * Since attachments are introduced so a comment minimum criteria is either
     * it should have a comment text or should have some attachments.
     */
    const isMinimumCommentCriteriaMissed =
      !processedCommentBody && checkIsArrayEmpty(attachments);

    if (isMinimumCommentCriteriaMissed) {
      notifyError("Comment cannot be empty!");
      return;
    }

    startProcessingComment();
    callEditApi({
      comment_uuid: commentData.uuid,
      updated_values: {
        comment: processedCommentBody,
        attachments,
        status: status,
      },
    })
      .then(() => {
        /** Updating highlighted Comment and reply in case they are updated */
        if (
          setHighlightedComment &&
          commentData.uuid == highlightedComment?.uuid
        )
          setHighlightedComment({
            ...highlightedComment,
            status: status || COMMENT_STATUS.SHOWN,
            comment: processedCommentBody,
            attachments,
          });
        else if (
          setHighlightedReply &&
          commentData.uuid == highlightedReply?.uuid
        )
          setHighlightedReply({
            ...highlightedReply,
            status: status || COMMENT_STATUS.SHOWN,
            comment: processedCommentBody,
            attachments,
          });
        else {
          const newData = [...data].map((i) => {
            if (i.uuid !== commentData.uuid) {
              return i;
            }

            return {
              ...i,
              status: status || COMMENT_STATUS.SHOWN,
              comment: processedCommentBody,
              attachments,
            };
          });
          setData(newData);
        }
        successCallback();
        /**Avoid updated notificaiton in case of status update*/
        if (!status) notifySuccess("Updated!");
      })
      .catch((e) => {
        notifyError(
          getErrorMessageFromApiError(e) ||
            "An error occured, please try after some time",
        );
      })
      .finally(stopProcessingComment);
  };

  const onDelete: IUseCommentsList.IUseCommentsListReturn["onDelete"] = ({
    commentData,
  }) => {
    if (
      !(
        isCreator || // creator can delete any comment
        userUuid === commentData.commenter_username
      )
    )
      return;
    if (isProcessingComment) return;
    startProcessingComment();
    callDeleteApi({ comment_uuid: commentData.uuid })
      .then(() => {
        notifySuccess("Deleted!");
        /** Deleting highlighted Comment and reply in case they are updated */
        if (
          setHighlightedComment &&
          commentData.uuid == highlightedComment?.uuid
        )
          setHighlightedComment(undefined);
        else if (
          setHighlightedReply &&
          commentData.uuid == highlightedReply?.uuid
        )
          setHighlightedReply(undefined);
        else {
          const newData = [...data].filter((i) => i.uuid !== commentData.uuid);
          setData(newData);
        }

        if (totalComments) {
          setTotalComments(totalComments - 1);
        }

        /** DEV - Demo Pointer that in case we start deleting comments and
         * length of comments reach less than initial size here case is 3 for better
         * UX we should automatically loadMore if there are more comments available to avoid user efforts */
        if (
          data.length < page_sizes.first_call &&
          data.length < (totalComments ?? 0)
        )
          loadComments();
      })
      .catch((e) => {
        notifyError(
          getErrorMessageFromApiError(e) ||
            "An error occured, please try after some time",
        );
      })
      .finally(stopProcessingComment);
  };

  return {
    onSubmit,
    data,
    userUuid,
    isCreator,
    totalComments,
    showLoadMoreCommentsBtn: data.length < (totalComments ?? 0),
    loadComments,
    reloadComments,
    onLikeClick,
    onUpdate,
    onDelete,
  };
};
